import { Badge, Button, IconButton, Modal, Surface, Table, Td, Th, Tr } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Restaurant, RestaurantCreate, RestaurantDetail, RestaurantUser } from '@warehouse/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { usePermission } from '../../../auth/auth-store';
import { useConfirm } from '../../../components/ConfirmDialog';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { FullPageSpinner } from '../../../components/FullPageSpinner';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { formatDateTime } from '../../../lib/format';
import { listQueryKey } from '../../../lib/list-query';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import {
  createRestaurant,
  getRestaurant,
  getRestaurantHistory,
  getRestaurantUsers,
  listRestaurants,
  unlinkRestaurantUser,
  updateRestaurant,
  type RestaurantListRow,
} from '../api';
import { BcBadge, BcSyncNotice, isBcLocked } from '../components/BcBadge';
import { EntityHistory } from '../components/EntityHistory';
import { LoadFailure } from '../components/LoadFailure';
import { CheckboxField, FormSection, TextField } from '../components/form';
import { Tabs, useTabState } from '../components/Tabs';

interface RestaurantFormValues {
  name: string;
  code: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  countryCode: string;
  contactEmail: string;
  contactPhone: string;
  isActive: boolean;
}

const EMPTY: RestaurantFormValues = {
  name: '',
  code: '',
  addressLine1: '',
  addressLine2: '',
  postalCode: '',
  city: '',
  countryCode: '',
  contactEmail: '',
  contactPhone: '',
  isActive: true,
};

const nullable = (value: string): string | null => (value.trim().length === 0 ? null : value.trim());

function toPayload(values: RestaurantFormValues): RestaurantCreate {
  return {
    name: values.name.trim(),
    code: values.code.trim(),
    addressLine1: nullable(values.addressLine1),
    addressLine2: nullable(values.addressLine2),
    postalCode: nullable(values.postalCode),
    city: nullable(values.city),
    countryCode: nullable(values.countryCode),
    contactEmail: nullable(values.contactEmail),
    contactPhone: nullable(values.contactPhone),
    isActive: values.isActive,
  } as RestaurantCreate;
}

/**
 * Restaurant register (spec §5.13).
 *
 * A `bestallare` reaching this list gets only their own restaurants — the
 * filtering happens server-side in `RestaurantsService.scopeFor`, so this page
 * needs no role branching at all. That is the point: the UI shows what the API
 * returned, and the API returned what the caller is allowed to see.
 */
export function RestaurantsPage(): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const navigate = useNavigate();
  const { state, setState } = useListState({ defaultSort: 'name', defaultDir: 'asc' });
  const canManage = usePermission('registers.restaurants.manage');
  const [creating, setCreating] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['master-data', 'restaurants', listQueryKey(state)],
    queryFn: () => listRestaurants(state),
  });
  useLiveQuery(['restaurants'], ['master-data', 'restaurants']);

  const columns: DataTableColumn<RestaurantListRow>[] = [
    { id: 'name', header: t('masterData:restaurants.columns.name'), sortField: 'name', cell: (row) => row.name },
    {
      id: 'code',
      header: t('masterData:restaurants.columns.code'),
      sortField: 'code',
      cell: (row) => <span className="font-mono text-xs">{row.code}</span>,
    },
    {
      id: 'bcCustomerNo',
      header: t('masterData:restaurants.columns.bcCustomerNo'),
      hideBelow: 'md',
      cell: (row) => (row.bcCustomerNo ? <BcBadge bc={{ synced: true, bcNo: row.bcCustomerNo }} /> : '—'),
    },
    { id: 'city', header: t('masterData:restaurants.columns.city'), sortField: 'city', hideBelow: 'lg', cell: (row) => row.city ?? '—' },
    {
      id: 'users',
      header: t('masterData:restaurants.columns.users'),
      align: 'right',
      hideBelow: 'lg',
      cell: (row) => row.userCount,
    },
    {
      id: 'active',
      header: t('masterData:restaurants.columns.active'),
      sortField: 'isActive',
      cell: (row) =>
        row.isActive ? (
          <Badge tone="success">{t('common:common.active')}</Badge>
        ) : (
          <Badge tone="warning">{t('common:common.inactive')}</Badge>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('masterData:restaurants.title')}
        subtitle={t('masterData:restaurants.subtitle')}
        actions={
          canManage ? (
            <Button
              icon={<Plus />}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('masterData:restaurants.new')}
            </Button>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={data?.total ?? null}
        loading={isLoading}
        error={error}
        searchPlaceholder={t('masterData:restaurants.searchPlaceholder')}
        emptyTitle={t('masterData:restaurants.empty')}
        emptyDescription={t('masterData:restaurants.emptyDescription')}
        onRowClick={(row) => {
          void navigate(`/registers/restaurants/${row.id}`);
        }}
      />

      {creating ? (
        <RestaurantModal
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(restaurant) => {
            setCreating(false);
            void navigate(`/registers/restaurants/${restaurant.id}`);
          }}
        />
      ) : null}
    </>
  );
}

function RestaurantModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (restaurant: Restaurant) => void;
}): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (values: RestaurantFormValues) => createRestaurant(toPayload(values)),
    onSuccess: (restaurant) => {
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
      toastSuccess(t('masterData:restaurants.created'));
      onCreated(restaurant);
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('masterData:restaurants.new')} width={700}>
      <RestaurantForm
        defaultValues={EMPTY}
        submitting={mutation.isPending}
        onCancel={onClose}
        onSubmit={(values) => {
          mutation.mutate(values);
        }}
      />
    </Modal>
  );
}

/** Restaurant detail: Detaljer / Användare / Historik. */
export function RestaurantDetailPage(): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const { id = '' } = useParams();
  const [tab, setTab] = useTabState('details');
  const canManage = usePermission('registers.restaurants.manage');
  const queryClient = useQueryClient();

  const {
    data: restaurant,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['master-data', 'restaurant', id],
    queryFn: () => getRestaurant(id),
    enabled: Boolean(id),
  });
  useLiveQuery(['restaurants'], ['master-data', 'restaurant', id]);

  const mutation = useMutation({
    mutationFn: (values: RestaurantFormValues) => updateRestaurant(id, toPayload(values)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
      toastSuccess(t('masterData:restaurants.saved'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  if (error) return <LoadFailure error={error} />;
  if (isLoading || !restaurant) return <FullPageSpinner />;

  return (
    <>
      <PageHeader
        breadcrumb={<Link to="/registers/restaurants">{t('masterData:restaurants.title')}</Link>}
        title={restaurant.name}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral">{restaurant.code}</Badge>
            <BcBadge bc={restaurant.bc} />
            {!restaurant.isActive ? <Badge tone="warning">{t('common:common.inactive')}</Badge> : null}
          </div>
        }
      />

      <Tabs
        tabs={[
          { id: 'details', label: t('masterData:restaurants.tabs.details') },
          { id: 'users', label: t('masterData:restaurants.tabs.users'), badge: restaurant.userCount },
          { id: 'history', label: t('masterData:restaurants.tabs.history') },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'details' ? (
        <Surface className="p-6">
          <RestaurantForm
            defaultValues={{
              name: restaurant.name,
              code: restaurant.code,
              addressLine1: restaurant.addressLine1 ?? '',
              addressLine2: restaurant.addressLine2 ?? '',
              postalCode: restaurant.postalCode ?? '',
              city: restaurant.city ?? '',
              countryCode: restaurant.countryCode ?? '',
              contactEmail: restaurant.contactEmail ?? '',
              contactPhone: restaurant.contactPhone ?? '',
              isActive: restaurant.isActive,
            }}
            bc={restaurant.bc}
            readOnly={!canManage}
            submitting={mutation.isPending}
            onSubmit={(values) => {
              mutation.mutate(values);
            }}
          />
        </Surface>
      ) : null}

      {tab === 'users' ? <RestaurantUsersTab restaurantId={id} /> : null}

      {tab === 'history' ? (
        <EntityHistory
          queryKey={['master-data', 'restaurant', id, 'history']}
          fetch={() => getRestaurantHistory(id)}
          topics={['restaurants']}
        />
      ) : null}
    </>
  );
}

function RestaurantUsersTab({ restaurantId }: { restaurantId: string }): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const canManage = usePermission('registers.restaurantUsers.manage');
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();

  const { data } = useQuery({
    queryKey: ['master-data', 'restaurant', restaurantId, 'users'],
    queryFn: () => getRestaurantUsers(restaurantId),
    enabled: canManage,
  });

  const unlink = useMutation({
    mutationFn: (userId: string) => unlinkRestaurantUser(restaurantId, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
      toastSuccess(t('masterData:restaurantUsers.removed'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const users: RestaurantUser[] = data ?? [];

  return (
    <>
      {dialog}
      <Surface className="overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <Table>
            <thead>
              <tr className="border-b border-surface-border">
                <Th>{t('masterData:restaurantUsers.columns.user')}</Th>
                <Th>{t('masterData:restaurantUsers.columns.email')}</Th>
                <Th>{t('masterData:restaurantUsers.columns.roles')}</Th>
                <Th>{t('masterData:restaurantUsers.columns.createdAt')}</Th>
                <Th className="w-16" />
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {users.map((user) => (
                <Tr key={user.userId}>
                  <Td>{`${user.firstName} ${user.lastName}`.trim() || '—'}</Td>
                  <Td className="text-sm">{user.email}</Td>
                  <Td>
                    <span className="flex flex-wrap gap-1">
                      {user.roleKeys.map((role) => (
                        <Badge key={role} tone="neutral" size="sm">
                          {role}
                        </Badge>
                      ))}
                    </span>
                  </Td>
                  <Td className="text-xs text-ink-muted">{formatDateTime(user.createdAt)}</Td>
                  <Td>
                    {canManage ? (
                      <IconButton
                        variant="danger"
                        aria-label={t('common:actions.remove')}
                        onClick={() => {
                          void (async () => {
                            const ok = await confirm({
                              title: t('masterData:restaurantUsers.confirmRemove.title'),
                              message: t('masterData:restaurantUsers.confirmRemove.message', {
                                email: user.email,
                                restaurant: user.restaurantName,
                              }),
                            });
                            if (ok) unlink.mutate(user.userId);
                          })();
                        }}
                      >
                        <Trash2 className="size-4" />
                      </IconButton>
                    ) : null}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
        {users.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-ink-muted">
            {t('masterData:restaurantUsers.empty')}
          </p>
        ) : null}
      </Surface>
    </>
  );
}

function RestaurantForm({
  defaultValues,
  bc,
  readOnly = false,
  submitting,
  onSubmit,
  onCancel,
}: {
  defaultValues: RestaurantFormValues;
  bc?: RestaurantDetail['bc'];
  readOnly?: boolean;
  submitting: boolean;
  onSubmit: (values: RestaurantFormValues) => void;
  onCancel?: () => void;
}): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const { register, handleSubmit, formState } = useForm<RestaurantFormValues>({ defaultValues });

  return (
    <form
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event);
      }}
      className="flex flex-col gap-7"
      noValidate
    >
      {bc ? <BcSyncNotice bc={bc} /> : null}

      <FormSection title={t('masterData:restaurants.sections.identity')}>
        <TextField
          label={t('masterData:restaurants.fields.name')}
          disabled={readOnly || isBcLocked(bc, 'name')}
          hint={isBcLocked(bc, 'name') ? t('masterData:bc.fieldHint') : undefined}
          error={formState.errors.name ? t('common:common.required') : undefined}
          {...register('name', { required: true })}
        />
        <TextField
          label={t('masterData:restaurants.fields.code')}
          hint={t('masterData:restaurants.hints.code')}
          disabled={readOnly}
          error={formState.errors.code ? t('common:common.required') : undefined}
          {...register('code', { required: true })}
        />
        <CheckboxField
          label={t('masterData:restaurants.fields.isActive')}
          disabled={readOnly}
          {...register('isActive')}
        />
      </FormSection>

      <FormSection title={t('masterData:restaurants.sections.contact')}>
        <TextField
          label={t('masterData:restaurants.fields.contactEmail')}
          type="email"
          disabled={readOnly}
          {...register('contactEmail')}
        />
        <TextField
          label={t('masterData:restaurants.fields.contactPhone')}
          disabled={readOnly}
          {...register('contactPhone')}
        />
      </FormSection>

      <FormSection title={t('masterData:restaurants.sections.address')}>
        <TextField label={t('masterData:restaurants.fields.addressLine1')} disabled={readOnly} {...register('addressLine1')} />
        <TextField label={t('masterData:restaurants.fields.addressLine2')} disabled={readOnly} {...register('addressLine2')} />
        <TextField label={t('masterData:restaurants.fields.postalCode')} disabled={readOnly} {...register('postalCode')} />
        <TextField label={t('masterData:restaurants.fields.city')} disabled={readOnly} {...register('city')} />
        <TextField label={t('masterData:restaurants.fields.countryCode')} disabled={readOnly} {...register('countryCode')} />
      </FormSection>

      {readOnly ? null : (
        <div className="flex justify-end gap-2">
          {onCancel ? (
            <Button type="button" variant="ghost" onClick={onCancel}>
              {t('common:actions.cancel')}
            </Button>
          ) : null}
          <Button type="submit" loading={submitting}>
            {t('common:actions.save')}
          </Button>
        </div>
      )}
    </form>
  );
}
